import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { AdapterDayjs } from "@mui/x-date-pickers-6/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers-6/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers-6/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers-6/TimePicker";
import format from "date-fns/format";
import dayjs from "dayjs";
import React, { useState } from "react";

import LoadingButton from "../../../../../../design/wilya-loading-button";
import { getTimeForTimePicker } from "../../../../../../utils/helpers";
import SectionWrapper from "../../../../section-wrapper";
import { LocationLimitsAndControls } from "../../../../store/gat-admin-slice-types";
import {
	MAX_CONTINUOUS_SIGNUP_HOURS,
	MAX_SHIFT_DURATION_HOURS,
	MIN_CONTINUOUS_SIGNUP_HOURS,
	MIN_SHIFT_DURATION_HOURS,
} from "../../../limits-controls-bounds";
import SectionActions from "../../../section-actions";
import useLocationLimitsControlsController from "./controllers/use-location-limits-controls-controller";
import WeeklyHourLimits from "./weekly-hour-limits";

interface INumberFieldProps {
	id: string;
	title: string;
	subtitle: string;
	value: number;
	helperText: string;
	// Replaces the helper text and marks the field invalid when set.
	error?: string;
	onChange: (value: number) => void;
	inputProps?: object;
}

const NumberField = ({
	id,
	title,
	subtitle,
	value,
	helperText,
	error,
	onChange,
	inputProps,
}: INumberFieldProps) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			width: "260px",
		}}
	>
		<Typography
			sx={{
				fontFamily: "Roboto",
				fontWeight: 400,
				fontSize: "16px",
				color: "rgba(0, 0, 0, 0.87)",
			}}
		>
			{title}
		</Typography>
		<Typography
			sx={{
				fontFamily: "Roboto",
				fontWeight: 400,
				fontSize: "12px",
				color: "rgba(0, 0, 0, 0.6)",
				paddingBottom: "8px",
			}}
		>
			{subtitle}
		</Typography>
		<TextField
			id={id}
			variant='filled'
			type='number'
			label=''
			onFocus={(event) => {
				event.target.select();
			}}
			onChange={(e) => onChange(parseInt(e.target.value))}
			value={Number.isFinite(value) ? value : ""}
			inputProps={inputProps}
			error={Boolean(error)}
			helperText={error || helperText}
		/>
	</Box>
);

const LocationLimitsControls = ({ locationData, companyId }: any) => {
	const [
		{
			limitControl,
			newLimitControl,
			getStatus,
			updateStatus,
			updateErrorMessage,
			resetStatus,
			resetErrorMessage,
			validationError,
			shiftLimitErrors,
			workerSubTypeEnabled,
		},
		{
			setNewLimitControl,
			handleWorkerTypeHoursChange,
			getWorkerTypeName,
			handleSave,
			handleResetToCompany,
			closeUpdateSnackbar,
			closeResetSnackbar,
			closeValidationSnackbar,
		},
	] = useLocationLimitsControlsController(locationData?.id, companyId);

	const [resetDialogOpen, setResetDialogOpen] = useState(false);

	const setField = (changes: Partial<LocationLimitsAndControls>) => {
		setNewLimitControl((prev) => (prev ? { ...prev, ...changes } : prev));
	};

	if (getStatus === "pending" || !newLimitControl) {
		return (
			<SectionWrapper
				title='Limits & Controls'
				subtitle='Decide on different checks placed for this location.'
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						padding: "32px",
					}}
				>
					<CircularProgress size={24} />
					<Typography sx={{ ml: 2 }}>Loading...</Typography>
				</Box>
			</SectionWrapper>
		);
	}

	return (
		<SectionWrapper
			title='Limits & Controls'
			subtitle='Decide on different checks placed for this location.'
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					marginTop: "16px",
				}}
			>
				<LoadingButton
					variant='outlined'
					color='error'
					loading={resetStatus === "pending"}
					onClick={() => setResetDialogOpen(true)}
				>
					Reset to company defaults
				</LoadingButton>
			</Box>
			<Box
				sx={{
					marginTop: "16px",
				}}
			>
				<Typography
					sx={{
						fontFamily: "Roboto",
						fontWeight: 400,
						fontSize: "16px",
						color: "rgba(0, 0, 0, 0.87)",
						marginBottom: "4px",
					}}
				>
					Start time and first day of the standard week
				</Typography>
				<Box display='flex'>
					<Box
						sx={{
							"& .MuiTextField-root": {
								width: "100px",
							},
						}}
						mr={2}
					>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DemoContainer components={["TimePicker"]}>
								<TimePicker
									label='Start Time (required)'
									slotProps={{
										textField: {
											variant: "filled",
											InputLabelProps: {
												shrink: true,
											},
											sx: {
												minWidth: "168px !important",
											},
										},
									}}
									onChange={(newValue) => {
										try {
											if (JSON.stringify(newValue).length !== 4) {
												setField({
													workWeekStartTime: getTimeForTimePicker(newValue),
												});
											} else if (newValue === null) {
												setField({ workWeekStartTime: "" });
											}
										} catch (e) {
											// Do nothing
										}
									}}
									value={dayjs(
										`${format(new Date(), "yyyy-MM-dd")}T${
											newLimitControl.workWeekStartTime || "00:00:00"
										}`
									)}
								/>
							</DemoContainer>
						</LocalizationProvider>
						<Typography
							sx={{
								fontFamily: "Roboto",
								fontWeight: 400,
								fontSize: "12px",
								color: "rgba(0, 0, 0, 0.6)",
								marginTop: "4px",
								marginLeft: "16px",
							}}
						>
							Hours:Minutes AM/PM
						</Typography>
					</Box>

					<FormControl
						variant='filled'
						sx={{ width: "160px", marginTop: "8px" }}
					>
						<InputLabel id='location-limits-week-start-day'>
							First day of the week
						</InputLabel>
						<Select
							labelId='location-limits-week-start-day'
							onChange={(e) => {
								setField({ workWeekStartDay: e.target.value });
							}}
							value={newLimitControl.workWeekStartDay ?? "Monday"}
							variant='filled'
						>
							<MenuItem value='Monday'>Monday</MenuItem>
							<MenuItem value='Tuesday'>Tuesday</MenuItem>
							<MenuItem value='Wednesday'>Wednesday</MenuItem>
							<MenuItem value='Thursday'>Thursday</MenuItem>
							<MenuItem value='Friday'>Friday</MenuItem>
							<MenuItem value='Saturday'>Saturday</MenuItem>
							<MenuItem value='Sunday'>Sunday</MenuItem>
						</Select>
					</FormControl>
				</Box>
			</Box>

			<Box
				sx={{
					backgroundColor: "#F5F5F5",
					borderRadius: "8px",
					marginTop: "32px",
					padding: "16px",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "16px",
							color: "rgba(0, 0, 0, 0.87)",
						}}
					>
						Allow to Offset Scheduler Based on Location Start Day
					</Typography>
					<Switch
						disabled={getStatus === "pending"}
						checked={newLimitControl.schedulerOffsetEnabled ?? false}
						onChange={(e) =>
							setField({ schedulerOffsetEnabled: e.target.checked })
						}
					/>
				</Box>
			</Box>

			<Box
				sx={{
					backgroundColor: "#F5F5F5",
					borderRadius: "8px",
					marginTop: "32px",
					padding: "16px",
				}}
			>
				<Box mb={2}>
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "12px",
							color: "rgba(0, 0, 0, 0.6)",
						}}
					>
						Below settings are only applicable for flex shifts (flex worker) or
						overtime shifts (full time worker)
					</Typography>
				</Box>
				<Grid container>
					<Grid item xs={4}>
						<NumberField
							id='location-default-shift-duration'
							title='Default shift duration'
							subtitle='Shift duration when a new shift is created.'
							value={newLimitControl.defaultShiftDuration}
							helperText='Hours'
							error={shiftLimitErrors.defaultDuration}
							inputProps={{
								min: MIN_SHIFT_DURATION_HOURS,
								max: MAX_SHIFT_DURATION_HOURS,
							}}
							onChange={(value) => setField({ defaultShiftDuration: value })}
						/>
					</Grid>
					<Grid item xs={4}>
						<NumberField
							id='location-min-shift-duration'
							title='Min. Shift duration'
							subtitle='Lowest single shift duration.'
							value={newLimitControl.minShiftDuration}
							helperText='Hours'
							error={shiftLimitErrors.minDuration}
							inputProps={{
								min: MIN_SHIFT_DURATION_HOURS,
								max: MAX_SHIFT_DURATION_HOURS,
							}}
							onChange={(value) => setField({ minShiftDuration: value })}
						/>
					</Grid>
					<Grid item xs={4}>
						<NumberField
							id='location-max-shift-duration'
							title='Max. Shift duration'
							subtitle='Highest single shift duration.'
							value={newLimitControl.maxShiftDuration}
							helperText='Hours'
							error={shiftLimitErrors.maxDuration}
							inputProps={{
								min: MIN_SHIFT_DURATION_HOURS,
								max: MAX_SHIFT_DURATION_HOURS,
							}}
							onChange={(value) => setField({ maxShiftDuration: value })}
						/>
					</Grid>
				</Grid>
			</Box>

			<Grid
				container
				sx={{
					marginTop: "32px",
					paddingLeft: "16px",
				}}
			>
				<Grid item xs={4}>
					<NumberField
						id='location-max-continuous-signup-hours'
						title='Maximum continuous signup hours'
						subtitle='Worker can signup shifts for these many hours back to back.'
						value={newLimitControl.maxContiniousSignUpHours}
						helperText='Hours (0 means no limit)'
						error={shiftLimitErrors.maxContinuousHours}
						inputProps={{
							min: MIN_CONTINUOUS_SIGNUP_HOURS,
							max: MAX_CONTINUOUS_SIGNUP_HOURS,
						}}
						onChange={(value) =>
							setField({ maxContiniousSignUpHours: value })
						}
					/>
				</Grid>
				<Grid item xs={4}>
					<NumberField
						id='location-max-signup-in-24-hours'
						title='Maximum signup hours per day'
						subtitle='Shift hours that a worker can work with or without breaks in any 24 hour period.'
						value={newLimitControl.maxWorkHoursInADay}
						helperText='Hours'
						inputProps={{ min: 1, max: 24 }}
						onChange={(value) => {
							if (value >= 1 && value <= 24) {
								setField({ maxWorkHoursInADay: value });
							}
						}}
					/>
				</Grid>
				<Grid item xs={4}>
					<NumberField
						id='location-max-continuous-days'
						title='Maximum continuous signup days'
						subtitle='Worker can signup for Shifts for these many days back to back'
						value={newLimitControl.maxContinuousDays}
						helperText='Days'
						inputProps={{ min: 0, max: 31 }}
						onChange={(value) => {
							setField({
								maxContinuousDays: Math.min(isNaN(value) ? 0 : value, 31),
							});
						}}
					/>
				</Grid>
			</Grid>

			<WeeklyHourLimits
				workerTypeHours={newLimitControl.workerTypeHours ?? []}
				workerSubTypeEnabled={workerSubTypeEnabled}
				getWorkerTypeName={getWorkerTypeName}
				onHoursChange={handleWorkerTypeHoursChange}
			/>

			<SectionActions
				oldData={limitControl}
				newData={newLimitControl}
				resetChanges={() => {
					setNewLimitControl(limitControl);
				}}
				handleSave={handleSave}
				canNotSave={Object.keys(shiftLimitErrors).length > 0}
				status={updateStatus}
				loading={updateStatus === "pending"}
			/>

			<Dialog
				open={resetDialogOpen}
				onClose={() => setResetDialogOpen(false)}
				aria-labelledby='reset-to-company-dialog-title'
			>
				<DialogTitle id='reset-to-company-dialog-title'>
					Reset to company defaults?
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This will discard all Limits &amp; Controls customisation for this
						location and replace it with the company&apos;s current settings.
						This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
					<Button
						color='error'
						variant='contained'
						onClick={() => {
							setResetDialogOpen(false);
							handleResetToCompany();
						}}
					>
						Reset
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={updateStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={closeUpdateSnackbar}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Location Limits &amp; Controls updated successfully
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={updateStatus === "error"}
				autoHideDuration={6000}
				onClose={closeUpdateSnackbar}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert
					elevation={6}
					variant='filled'
					severity='error'
					onClose={closeUpdateSnackbar}
				>
					{updateErrorMessage || "An error occurred. Please try again."}
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={resetStatus === "fulfilled"}
				autoHideDuration={3000}
				onClose={closeResetSnackbar}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant='filled' severity='success'>
					Location reset to company defaults
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={resetStatus === "error"}
				autoHideDuration={6000}
				onClose={closeResetSnackbar}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert
					elevation={6}
					variant='filled'
					severity='error'
					onClose={closeResetSnackbar}
				>
					{resetErrorMessage || "An error occurred. Please try again."}
				</MuiAlert>
			</Snackbar>
			<Snackbar
				open={validationError !== ""}
				autoHideDuration={6000}
				onClose={closeValidationSnackbar}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert
					elevation={6}
					variant='filled'
					severity='error'
					onClose={closeValidationSnackbar}
				>
					{validationError}
				</MuiAlert>
			</Snackbar>
		</SectionWrapper>
	);
};

export default LocationLimitsControls;
